import React, { useEffect, useRef, useState } from 'react';
import './ProcessFlatRates.css';
import api from '../wrapper/API';
import ActionButton from '../input/ActionButton';
import FlatRateRowsEditor, { flatRateRowErrors, nextFlatRateRowKey } from '../flat-rate/FlatRateRowsEditor';
import { useSuccessBar } from '../context/SuccessBarContext';
import useFlatRates from '../../hooks/useFlatRates';
import useFlatRateRecipients from '../../hooks/useFlatRateRecipients';
import useRoleSettings from '../../hooks/useRoleSettings';
import { colorVar, normalizeRole, roleColorKey } from '../../util/participantGroups';
import { formatEuro, parsePriceInput, toPriceInput } from '../../util/eventPrices';
import {
  FLAT_RATE_KIND, dueLabel, formatFlatRateSummary, isDeduction, normalizeFlatRateKind, recipientLabel,
} from '../../util/flatRates';
import ProcessNotice from './ProcessNotice';
import { apiErrorMessage, eventNouns } from './processUtils';

// ProcessFlatRateDto → editable row (row model: components/flat-rate/FlatRateRowsEditor). Deductions
// never carry a recipient.
function toRow(flatRate) {
  const kind = normalizeFlatRateKind(flatRate.kind);
  const deduction = kind === FLAT_RATE_KIND.deduction;
  return {
    key: nextFlatRateRowKey(),
    id: flatRate.id,
    kind,
    flatRateId: flatRate.flatRateId != null ? Number(flatRate.flatRateId) : null,
    role: deduction ? null : normalizeRole(flatRate.role),
    playerId: !deduction && flatRate.playerId != null ? Number(flatRate.playerId) : null,
    amount: toPriceInput(flatRate.amount),
    flatRateName: flatRate.flatRateName ?? '',
    recipientName: deduction ? '' : (flatRate.recipientName ?? ''),
    catalogAmount: null,
  };
}

const recipientIcon = (flatRate) => {
  if (normalizeRole(flatRate.role)) return 'shield_person';
  return flatRate.playerId == null ? 'person_off' : 'person';
};

/**
 * "Pauschalen & Abzüge anpassen": edit the flat-rate and deduction rows of ONE process (copied from its
 * template with the catalog amount of the process location at creation) — [recipient,] catalog entry and
 * amount per row; also on closed processes, the statistics read the rows live. Two areas (Pauschalen,
 * Abzüge), one save → PUT /EventProcess/{id}/FlatRates with one list (Pauschalen first, then Abzüge).
 * Shown inline instead of the list.
 */
function ProcessFlatRatesEditor({ process, catalog, recipients, onSaved, onCancel }) {
  const { startSuccessBarLoading, startSuccessBarSuccess, closeSuccessBar } = useSuccessBar();
  const [flatRateRows, setFlatRateRows] = useState(() => (process.flatRates ?? []).filter(f => !isDeduction(f)).map(toRow));
  const [deductionRows, setDeductionRows] = useState(() => (process.flatRates ?? []).filter(isDeduction).map(toRow));
  const [showErrors, setShowErrors] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const topRef = useRef(null);
  const nouns = eventNouns(process);

  // Catalog + recipient candidates once, when the editor opens the first time (hooks live in the section).
  const ensureCatalog = catalog.ensureLoaded;
  const ensureRecipients = recipients.ensureLoaded;
  useEffect(() => {
    ensureCatalog();
    ensureRecipients();
  }, [ensureCatalog, ensureRecipients]);

  const handleSave = () => {
    if (saving) return;
    const rows = [...flatRateRows, ...deductionRows];
    setShowErrors(true);
    if (flatRateRowErrors(rows, { withAmount: true }).size > 0) {
      setError('Bitte die markierten Einträge prüfen.');
      topRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
      return;
    }
    setSaving(true);
    setError('');
    startSuccessBarLoading('Pauschalen & Abzüge werden gespeichert');
    api.put(`/EventProcess/${process.id}/FlatRates`, {
      flatRates: rows.map(r => {
        const deduction = isDeduction(r);
        const role = deduction ? null : normalizeRole(r.role);
        return {
          id: r.id ?? null,
          flatRateId: r.flatRateId,
          role,
          playerId: deduction || role ? null : (r.playerId ?? null),
          amount: parsePriceInput(r.amount),
        };
      }),
    })
      .then(response => {
        startSuccessBarSuccess('Pauschalen & Abzüge gespeichert');
        onSaved(response.data);
      })
      .catch(err => {
        closeSuccessBar();
        setError(apiErrorMessage(err, 'Pauschalen & Abzüge konnten nicht gespeichert werden.'));
        topRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
      })
      .finally(() => setSaving(false));
  };

  const closedWord = nouns.many === 'Spieltage' ? 'Spieltagen' : nouns.many;
  // Shared by both areas: the catalog amount of the process location is prefilled and hinted.
  const editorProps = {
    flatRates: catalog.flatRates,
    flatRatesLoaded: catalog.loaded,
    flatRatesFailed: catalog.failed,
    withAmount: true,
    showErrors,
    disabled: saving,
    locationId: process.locationId ?? null,
    locationName: process.locationName ?? '',
  };

  return (
    <div className={'processFlatRatesEditor'} ref={topRef}>
      <div className={'processFlatRatesEditorHeader'}>
        <h3>Pauschalen &amp; Abzüge anpassen</h3>
        <p className={'processActionDesc'}>
          Gilt nur für diesen Prozess, je {nouns.one}. Neue Einträge übernehmen den Katalogbetrag
          {process.locationName ? ` für ${process.locationName}` : ' der Prozess-Location'}. Änderungen wirken
          sofort auf die Statistik – auch bei abgeschlossenen {closedWord}.
        </p>
      </div>

      {error && <ProcessNotice tone={'error'}>{error}</ProcessNotice>}
      {catalog.failed && (
        <ProcessNotice tone={'warning'}>Pauschalen &amp; Abzüge konnten nicht geladen werden – die Auswahl ist leer.</ProcessNotice>
      )}
      {!catalog.failed && recipients.failed && (
        <ProcessNotice tone={'warning'}>Empfänger konnten nicht geladen werden – es stehen nur die Rollen zur Wahl.</ProcessNotice>
      )}

      <FlatRateRowsEditor
        {...editorProps}
        title={'Pauschalen'}
        kind={FLAT_RATE_KIND.flatRate}
        rows={flatRateRows}
        onChange={setFlatRateRows}
        candidates={recipients.candidates}
        candidatesById={recipients.byId}
        candidatesLoaded={recipients.loaded}
        candidatesFailed={recipients.failed}
        emptyText={'Keine Pauschalen – über „Pauschale hinzufügen“ eintragen.'}
      />

      <FlatRateRowsEditor
        {...editorProps}
        title={'Abzüge'}
        kind={FLAT_RATE_KIND.deduction}
        rows={deductionRows}
        onChange={setDeductionRows}
        emptyText={'Keine Abzüge – über „Abzug hinzufügen“ eintragen.'}
      />

      <div className={'processFlatRatesEditorFooter'}>
        <ActionButton icon={'close'} label={'Abbrechen'} onClick={onCancel} disabled={saving} />
        <ActionButton icon={'save'} label={'Speichern'} type={2} onClick={handleSave} disabled={saving} />
      </div>
    </div>
  );
}

/**
 * Section "Pauschalen & Abzüge" of the process page (between the header and the event list): one compact row per
 * flat rate (recipient, catalog entry, amount, when it is due), then — under "Abzüge" — one per deduction
 * (no recipient), and the per-event sum of both; "Anpassen" swaps in the inline editor — also on closed
 * processes (corrections after the season). The catalog and the recipient candidates are fetched when the
 * editor opens the first time and reused for every process. onSaved(EventProcessResponse) → the page
 * takes over the process.
 */
function ProcessFlatRates({ process, onSaved }) {
  useRoleSettings(); // role names / colors of role rows
  const catalog = useFlatRates();
  const recipients = useFlatRateRecipients();
  // Id of the process being edited — another selected process never shows the editor.
  const [editingId, setEditingId] = useState(null);
  const editing = editingId != null && editingId === process.id;

  // Leave edit mode when another process is selected (and do not reopen it on the way back).
  useEffect(() => { setEditingId(null); }, [process.id]);

  const flatRates = process.flatRates ?? [];
  const personRows = flatRates.filter(f => !isDeduction(f));
  const deductions = flatRates.filter(isDeduction);
  const summary = formatFlatRateSummary(flatRates);

  const renderFlatRate = (f) => {
    const role = normalizeRole(f.role);
    const deleted = !role && f.playerId == null;
    return (
      <li key={f.id} className={'processFlatRateRow'}>
        <span
          className={`material-symbols-rounded processFlatRateIcon${role ? ' isRole' : ''}`}
          style={role ? { '--process-flat-rate-color': colorVar(roleColorKey(role)) } : undefined}
          aria-hidden={'true'}
        >
          {recipientIcon(f)}
        </span>
        <span className={`processFlatRateRecipient${deleted ? ' isDeleted' : ''}`}>{recipientLabel(f)}</span>
        <span className={'processFlatRateName'}>{f.flatRateName}</span>
        <span className={'processFlatRateAmount'}>{formatEuro(f.amount)}</span>
        <span className={'processFlatRateDue'}>{dueLabel(f)}</span>
      </li>
    );
  };

  // Deduction: no recipient (the empty cell keeps the columns aligned with the flat rates above).
  const renderDeduction = (f) => (
    <li key={f.id} className={'processFlatRateRow isDeduction'}>
      <span className={'material-symbols-rounded processFlatRateIcon isDeduction'} aria-hidden={'true'}>remove_circle</span>
      <span className={'processFlatRateRecipient'} aria-hidden={'true'} />
      <span className={'processFlatRateName'}>{f.flatRateName}</span>
      <span className={'processFlatRateAmount'}>{formatEuro(f.amount)}</span>
      <span className={'processFlatRateDue'}>{dueLabel(f)}</span>
    </li>
  );

  return (
    <div className={'processFlatRatesSection'}>
      <div className={'processSectionHeader'}>
        <label className={'innerContainerLabel'}>Pauschalen &amp; Abzüge</label>
        {!editing && (
          <ActionButton
            icon={'tune'}
            label={'Anpassen'}
            type={3}
            onClick={() => setEditingId(process.id)}
            title={'Pauschalen & Abzüge anpassen'}
            ariaLabel={'Pauschalen & Abzüge anpassen'}
          />
        )}
      </div>

      {editing ? (
        <ProcessFlatRatesEditor
          key={process.id}
          process={process}
          catalog={catalog}
          recipients={recipients}
          onSaved={detail => { setEditingId(null); onSaved(detail); }}
          onCancel={() => setEditingId(null)}
        />
      ) : flatRates.length === 0 ? (
        <p className={'processFlatRatesEmpty'}>Keine Pauschalen oder Abzüge.</p>
      ) : (
        <div className={'processFlatRatesTable'}>
          {personRows.length > 0 && (
            <ul className={'processFlatRateList'} aria-label={'Pauschalen'}>
              {personRows.map(renderFlatRate)}
            </ul>
          )}
          {deductions.length > 0 && (
            <React.Fragment>
              <p className={`processFlatRateSubhead${personRows.length > 0 ? ' isAfterRows' : ''}`} aria-hidden={'true'}>
                Abzüge
              </p>
              <ul className={'processFlatRateList'} aria-label={'Abzüge'}>
                {deductions.map(renderDeduction)}
              </ul>
            </React.Fragment>
          )}
          <p className={'processFlatRateSum'}>
            <span className={'processFlatRateSumLabel'}>Summe</span>
            <span className={'processFlatRateSumValue'}>{summary}</span>
          </p>
        </div>
      )}
    </div>
  );
}

export default ProcessFlatRates;
